/**
 * Novidades do SGQ: as notas de atualização mostradas ao abrir o sistema, como as do Portal de
 * Chamados. Escreva para quem usa o SGQ no dia a dia, sem termos técnicos: o que mudou e o que a
 * pessoa ganha com isso. Uma entrada por novidade; o `id` nunca muda (é ele que marca o que já
 * foi visto).
 *
 * Quem abre o SGQ vê as novidades dos últimos NEWS_DAYS dias que ainda não viu.
 */

export type NewsItem = {
  id: string;
  /** Quando entrou no SGQ (ISO, horário de Brasília). */
  at: string;
  title: string;
  text: string;
  /** Só para quem tem esta permissão (sem ela: todo mundo). */
  permission?: string;
  /** Só para gestores da qualidade e administradores. */
  managers?: boolean;
};

export type NewsViewer = { role: string; permissions: string[] };

export const NEWS_DAYS = 7;

const managerRoles = ["quality_manager", "admin", "administrator"];

export const NEWS: NewsItem[] = [
  {
    id: "2026-10-18-dashboard",
    at: "2026-10-18T10:00:00-03:00",
    title: "Dashboard novo",
    text: "A antiga Visão geral virou Dashboard: PMs e NCs ao longo do tempo, PMs atrasados (mais de 15 dias em aberto) com acesso direto a cada um, e os tipos de manutenção. Clique num tipo, como Peça danificada, para ver quais produtos tiveram esse problema. Use a lista de período no topo para mudar o intervalo.",
    permission: "quality.dashboard.view",
  },
  {
    id: "2026-10-18-novidades",
    at: "2026-10-18T09:00:00-03:00",
    title: "Novidades da semana",
    text: "O botão ✨ no topo mostra o que mudou no SGQ nos últimos dias. Quando sair algo novo, esta janela abre sozinha na próxima vez que você entrar.",
  },
  {
    id: "2026-10-18-notificacoes-windows",
    at: "2026-10-18T09:00:00-03:00",
    title: "Avisos do SGQ no Windows",
    text: "Os avisos do SGQ agora podem aparecer no canto da tela, mesmo com o sistema fechado, e o clique abre o item direto. Para receber, vá em Configurações › Notificações, clique em Ativar em “Notificações do Windows” e escolha quais avisos quer. Gestores encontram essa opção ao editar a regra, no canal Windows.",
    permission: "quality.notifications.view",
  },
  {
    id: "2026-10-18-prazo-kanban",
    at: "2026-10-18T09:00:00-03:00",
    title: "Contador de dias para ao concluir",
    text: "Nos kanbans de Manutenções e de Não conformidades, o contador de dias do card para no dia em que ele entra em Concluído ou Cancelado. Se o card voltar para uma etapa em andamento, a contagem recomeça.",
  },
  {
    id: "2026-10-18-encerra-prazo",
    at: "2026-10-18T09:00:00-03:00",
    title: "Colunas que encerram o prazo",
    text: "Em Configurações › Modelos de Kanban, cada coluna tem a opção “Encerra prazo”. Marque nas colunas finais dos seus fluxos personalizados para o contador de dias parar quando o card chegar nelas.",
    managers: true,
  },
  {
    id: "2026-10-18-tipos-manutencao",
    at: "2026-10-18T09:00:00-03:00",
    title: "Tipos de manutenção sempre atualizados",
    text: "Um tipo criado em Gerenciar tipos já aparece para quem está preenchendo um PM, sem recarregar a página. E, com muitos tipos, a lista rola dentro da janela.",
    permission: "quality.customer_maintenance.view",
  },
  {
    id: "2026-10-18-permissoes",
    at: "2026-10-18T09:00:00-03:00",
    title: "Tela de permissões renovada",
    text: "Ao gerenciar um usuário, os módulos ficam numa lista com busca e filtros, dá para marcar todas as permissões de um módulo de uma vez, e o SGQ avisa antes de descartar alterações não salvas.",
    managers: true,
  },
  {
    id: "2026-10-18-topo",
    at: "2026-10-18T09:00:00-03:00",
    title: "Topo mais limpo",
    text: "A barra de busca e o botão de ajuda saíram do topo das páginas. Use o menu lateral para ir direto a cada módulo.",
  },
];

function forViewer(item: NewsItem, viewer: NewsViewer) {
  if (item.managers && !managerRoles.includes(viewer.role)) return false;
  if (item.permission && !viewer.permissions.includes("*") && !viewer.permissions.includes(item.permission)) return false;
  return true;
}

/** Novidades dos últimos dias para a pessoa, da mais nova para a mais antiga. */
export function recentNews(viewer: NewsViewer, now = Date.now(), list: NewsItem[] = NEWS) {
  const limit = now - NEWS_DAYS * 24 * 60 * 60 * 1000;
  return list
    .filter((item) => {
      const at = Date.parse(item.at);
      return Number.isFinite(at) && at >= limit && at <= now && forViewer(item, viewer);
    })
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
}

/** Nome do dia para agrupar: Hoje, Ontem ou o dia da semana com a data. */
export function newsDayLabel(iso: string, now = new Date()) {
  const day = new Date(iso);
  const key = (date: Date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (key(day) === key(now)) return "Hoje";
  if (key(day) === key(yesterday)) return "Ontem";
  return day.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "2-digit" });
}
